import { DateTime } from "luxon";
import { convertTimestampToLuxonOrDefaultNow } from "../../utils/convertTimestampToLuxonOrDefaultNow";
export function assertDraftRaveRecipient(draftRecipient) {
    if (!draftRecipient || typeof draftRecipient !== "object") {
        throw new Error("draftRecipient must be an object");
    }
    const r = draftRecipient;
    if (typeof r.userId !== "string") {
        throw new Error("draftRecipient.userId must be a string");
    }
    // Check for required fields
    if (typeof r.codeId !== "string") {
        throw new Error("draftRecipient.codeId must be a string");
    }
    if (typeof r.points !== "number") {
        throw new Error("draftRecipient.points must be a number");
    }
    if (typeof r.fixed !== "boolean") {
        throw new Error("draftRecipient.fixed must be a boolean");
    }
    // Name is optional
    if (r.name !== undefined && typeof r.name !== "string") {
        throw new Error("draftRecipient.name must be a string if provided");
    }
}
export function assertDraftRaveStatus(status) {
    if (![
        "draft",
        "submitted",
        "submitting",
        "activated",
        "deactivated",
        "failed",
    ].includes(status)) {
        throw new Error("RaveStatus must be one of: draft, submitted, submitting, activated, deactivated, failed");
    }
}
export const assertDraftRave = (data, mode = "load") => {
    if (!data || typeof data !== "object") {
        throw new Error("DraftRave must be an object");
    }
    const draftRave = data;
    // Required fields based on mode
    const requiredFields = mode === "save"
        ? [
            "programId",
            "content",
            "recipientIds",
            "status",
            "title",
            "description",
        ]
        : [
            "id",
            "programId",
            "title",
            "description",
            "content",
            "recipientIds",
            "status",
            "createdAt",
            "modifiedAt",
        ];
    for (const field of requiredFields) {
        if (!(field in draftRave)) {
            throw new Error(`Rave is missing required field: ${field}`);
        }
    }
    // Type checks
    if (mode === "load" && typeof draftRave.id !== "string")
        throw new Error("draftRave.id must be a string");
    if (!Array.isArray(draftRave.recipientIds))
        throw new Error("draftRave.recipientIds must be an array");
    // Validate status
    assertDraftRaveStatus(draftRave.status);
    // Content validation
    const contents = draftRave.content;
    if (!Array.isArray(contents)) {
        throw new Error("draftRave.content must be an array");
    }
    // Timestamp validation based on mode
    if (mode === "load") {
        if (!(draftRave.createdAt instanceof DateTime)) {
            throw new Error("draftRave.createdAt must be a DateTime");
        }
        if (!(draftRave.modifiedAt instanceof DateTime))
            throw new Error("draftRave.modifiedAt must be a DateTime");
    }
    else {
        // For save mode, we need to allow either DateTime or server timestamp
        // First check if fields exist
        if (draftRave.createdAt !== undefined) {
            // createdAt can be a DateTime or a FieldValue.serverTimestamp()
            // For server timestamp, it would be an object with an internal structure
            // specific to Firebase. Since we don't have a direct way to check,
            // we'll accept any object that's not a DateTime as a potential FieldValue
            const isDateTime = draftRave.createdAt instanceof DateTime;
            const isPotentialFieldValue = typeof draftRave.createdAt === "object" &&
                draftRave.createdAt !== null &&
                !isDateTime;
            if (!isDateTime && !isPotentialFieldValue) {
                throw new Error("draftRave.createdAt must be a DateTime or FieldValue.serverTimestamp()");
            }
        }
        if (draftRave.modifiedAt !== undefined) {
            const isDateTime = draftRave.modifiedAt instanceof DateTime;
            const isPotentialFieldValue = typeof draftRave.modifiedAt === "object" &&
                draftRave.modifiedAt !== null &&
                !isDateTime;
            if (!isDateTime && !isPotentialFieldValue) {
                throw new Error("draftRave.modifiedAt must be a DateTime or FieldValue.serverTimestamp()");
            }
        }
    }
};
export const createDraftRaveConverter = (convertToFirestoreTimestampOrDefaultServerTime) => ({
    toFirestore(draftRave) {
        assertDraftRave(draftRave, "save");
        const { id, ...draftRaveWithoutId } = draftRave;
        const data = {
            ...draftRaveWithoutId,
            createdAt: convertToFirestoreTimestampOrDefaultServerTime(draftRave.createdAt),
            modifiedAt: convertToFirestoreTimestampOrDefaultServerTime(),
        };
        return data;
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        if (!data)
            throw new Error("Snapshot doesn't contain data");
        const draftRave = {
            ...data,
            id: snapshot.id,
            createdAt: convertTimestampToLuxonOrDefaultNow(data.createdAt),
            modifiedAt: convertTimestampToLuxonOrDefaultNow(data.modifiedAt),
        };
        assertDraftRave(draftRave, "load");
        return draftRave;
    },
});
export const createDraftRave = (data) => {
    assertDraftRave(data, "save");
    return data;
};
//# sourceMappingURL=DraftRave.js.map